<template>
  <div class="card" v-show="currentCardId == 'requests'">
    <div class="overflow-scroll-y">
      <div class="card-header">
        <h2>本人希望欄を入力</h2>
        <p>給与・職種・勤務時間・勤務地などの希望を入力します（任意）</p>
        <p v-if="formatId == 'rirekisho_job_change_old'">
          ですが、<span style="color: #e3165c"
            >基本的には面接で話されることが多いため、「貴社規定に従います。」と記載しておくことが一般的</span
          >です。
        </p>
      </div>
      <div class="add-item">
        <el-button type="success" @click="exampleVisible = true"
          >例文から追加</el-button
        >
      </div>
      <div class="card-content">
        <!-- 転職用履歴書は給与などの自由記述を一番上に置く -->
        <div
          v-if="isHopeListFormat"
          class="edit-form-item requests-card__free-text"
        >
          <el-input
            type="textarea"
            :autosize="{ minRows: 3, maxRows: 15 }"
            placeholder="給与・職種・勤務時間・勤務地など希望があれば入力"
            v-model="formatData.request"
            v-proofread
          />
        </div>
        <template v-if="isSalaryVariant">
          <div class="edit-form-item">
            <div class="item-name">現在の年収</div>
            <div class="item-body">
              <el-input
                class="input-xs"
                placeholder=""
                title="現在の年収"
                type="number"
                v-model="formatData.nowNensyu"
              />
              万円
            </div>
            <div class="item-name">希望年収</div>
            <div class="item-body">
              <el-input
                class="input-xs"
                placeholder=""
                title="希望年収"
                type="number"
                v-model="formatData.kibouNensyu"
              />
              万円
            </div>
            <div class="item-name">
              ※上記項目を入力した後、例文から追加ボタンをクリックして例文を選択すると自動的に反映されます。
            </div>
          </div>
          <br />
        </template>
        <PersonalRequestFields
          v-if="!isSalaryVariant"
          mode="fields"
          :format-data="formatData"
          :basic-data="basicData"
          :format-id="formatId || ''"
          :industry-data="industryData"
          :job-type-data="jobTypeData"
          :show-ai-card="true"
          :inline-ai="isHopeListFormat"
          @update-basic-data="onUpdateBasicData"
          @update-format-data="Object.assign(formatData, $event)"
        />
        <HopeConditionFields
          v-if="!isSalaryVariant && formatId === 'rirekisho_job_change'"
          :format-data="formatData"
          :bizreach-industry-groups="bizreachIndustryGroups"
          :bizreach-job-category-groups="bizreachJobCategoryGroups"
          :hope-area-default-code="hopeAreaDefaultCode"
          @update-format-data="Object.assign(formatData, $event)"
        />
        <NewRirekishoExampleSentenceDialog
          :value="exampleVisible"
          name="request-dialog"
          :items="requestExamples"
          show-overwrite-warning
          overwrite-warning="例文を選択すると、現在入力している文章は上書きされますのでご注意ください。"
          @input="exampleVisible = $event"
          @select="onSelectExample"
        />
        <el-input
          v-if="!isHopeListFormat"
          type="textarea"
          :autosize="{ minRows: 3, maxRows: 15 }"
          placeholder="給与・職種・勤務時間・勤務地など希望があれば入力"
          v-model="formatData.request"
          v-proofread
        />
        <PersonalRequestFields
          v-if="!isSalaryVariant"
          mode="ai-card"
          :format-data="formatData"
          :basic-data="basicData"
          :format-id="formatId || ''"
          :industry-data="industryData"
          :job-type-data="jobTypeData"
          :show-ai-card="true"
          :inline-ai="isHopeListFormat"
          :request-ai-generated="requestAiGenerated"
          :request-ai-loading="requestAiLoading"
          :request-ai-error="requestAiError"
          :bizreach-industry-groups="bizreachIndustryGroups"
          :bizreach-job-category-groups="bizreachJobCategoryGroups"
          :get-industry-name="getIndustryName"
          :get-job-category-name="getJobCategoryName"
          @update-basic-data="onUpdateBasicData"
          @update-format-data="Object.assign(formatData, $event)"
        />
        <div class="attention-limit character" v-if="previewErrors.request">
          <i class="el-icon-warning"></i>
          入力可能な文字数をオーバーしています。文章を短くするか、入力欄の大きい別の履歴書を選んでください。
        </div>
      </div>
      <div v-if="formatId == 'rirekisho_job_change_old'">
        <p class="guidetxt">本人希望欄の見本（参考に作成しましょう）</p>
        <img
          src="/static/img/rirekishoguide/guide08.png"
          alt="本人希望欄"
          class="guideimg"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { industryData } from '@/assets/formats/industryData'
import { jobTypeData } from '@/assets/formats/jobTypeData'
import PersonalRequestFields from '@/components/formats/RirekishoParts/PersonalRequestFields.vue'
import HopeConditionFields from '@/components/format-edit/rirekisho/HopeConditionFields.vue'
import NewRirekishoExampleSentenceDialog from '@/components/format-edit/new-rirekisho/NewRirekishoExampleSentenceDialog.vue'
import {
  useRirekishoFormatData,
  type RirekishoExampleSentence,
  type RirekishoExampleSentences
} from '@/composables/format-edit/useRirekishoFormatData'
import { useRirekishoRequestAi } from '@/composables/format-edit/useRirekishoRequestAi'
import { useHopeWorkMasters } from '@/composables/format-edit/useHopeWorkMasters'
import type { BasicData } from '@/utils/format-edit/basicData'
import {
  hopeAreaTextsToResolve,
  planHopeAreaAndTurnTime,
  syncHopeListHead
} from '@/utils/format-edit/hopeWorkCondition'

const props = withDefaults(
  defineProps<{
    currentCardId?: string
    formatId?: string
    basicData: BasicData
    previewErrors: Record<string, string>
    exampleSentences?: RirekishoExampleSentences
    variant?: 'full' | 'salary'
  }>(),
  {
    variant: 'full'
  }
)

const emit = defineEmits<{
  (e: 'update-basic-data', payload: Partial<BasicData>): void
}>()

const formatData = useRirekishoFormatData()
const exampleVisible = ref(false)
const isSalaryVariant = computed(() => props.variant === 'salary')
// 希望業種・希望職種（3件まで）の入力欄を出す書式。ここでは上の欄が正で、AI もそこへ入れる
const isHopeListFormat = computed(
  () => props.formatId === 'rirekisho_job_change'
)

const {
  requestAiGenerated,
  requestAiLoading,
  requestAiError,
  bizreachIndustryGroups,
  bizreachJobCategoryGroups,
  getIndustryName,
  getJobCategoryName,
  runAutoRequestIfNeeded
} = useRirekishoRequestAi({
  formatData,
  basicData: () => props.basicData,
  emitUpdateBasicData: (payload) => emit('update-basic-data', payload),
  enabled: props.variant !== 'salary',
  hopeLists: () => isHopeListFormat.value
})

const requestExamples = computed(() => props.exampleSentences?.request || [])

// 希望勤務地の初期値に採用した先方勤務地コード（注記の表示条件に使う）
const hopeAreaDefaultCode = ref('')
// 希望勤務地・転職希望時期の選択肢と住所→勤務地コードの判定（varuna）
const hopeMasters = useHopeWorkMasters()

// 本人希望欄に到達した時点で、未入力の項目に初期値を入れる。
// 項目ごとに一度だけ（または既に値がある状態を確認したら）でやめる。
// ユーザーが消したあとに埋め戻さないため。
// 入力欄（希望勤務地・転職時期・希望業種/職種の一覧）がある書式だけ。欄が無い書式で入れると、
// 本人が見ていない既定値で送客の判定を通して送ってしまう
function applyHopeFieldDefaults() {
  if (!isHopeListFormat.value) return
  // 希望勤務地・転職希望時期: 保存値を先方コードに正規化し（旧形式の値は varuna の判定へ寄せる）、
  // 空なら初期値（現住所の勤務地・「1ヶ月以内」）を入れる。マスタが読み込み中・失敗なら何もしない
  const { defaultAreaCode, ...hopeFields } = planHopeAreaAndTurnTime(
    hopeMasters.masters.value,
    {
      hopeWorkAreas: formatData.hopeWorkAreas,
      hopeAreaAutofilled: formatData.hopeAreaAutofilled,
      hopeTurnTime: formatData.hopeTurnTime,
      hopeTurnTimeAutofilled: formatData.hopeTurnTimeAutofilled
    },
    props.basicData.address1 || ''
  )
  Object.assign(formatData, hopeFields)
  if (defaultAreaCode) {
    hopeAreaDefaultCode.value = defaultAreaCode
  }

  // 希望業種・希望職種: AI推定の結果を1件目に入れる。
  // 希望側(kibou*)が空のことがあるので、現在の業種・職種(gyoushuCd/shokushuCd)にも落とす
  if (!formatData.hopeSelectAutofilled) {
    const industryCd =
      props.basicData.kibouGyoushuCd || props.basicData.gyoushuCd
    const jobCategoryCd =
      props.basicData.kibouShokushuCd || props.basicData.shokushuCd
    if (
      formatData.hopeIndustryCds.length > 0 ||
      formatData.hopeJobCategoryCds.length > 0
    ) {
      formatData.hopeSelectAutofilled = true
    } else if (industryCd || jobCategoryCd) {
      if (industryCd) {
        formatData.hopeIndustryCds = [industryCd]
      }
      if (jobCategoryCd) {
        formatData.hopeJobCategoryCds = [jobCategoryCd]
      }
      formatData.hopeSelectAutofilled = true
    }
  }
}

// 選択肢と、住所・旧形式の勤務地の判定を varuna から受け取ってから初期値を適用する。
// 受け取れるまでは希望業種・職種の初期値だけを入れる（保存値の勤務地・時期は触らない）
function refreshHopeFieldDefaults() {
  applyHopeFieldDefaults()
  if (!isHopeListFormat.value) return
  hopeMasters
    .ensure(
      hopeAreaTextsToResolve(
        props.basicData.address1 || '',
        formatData.hopeWorkAreas
      )
    )
    .then(() => {
      if (props.currentCardId === 'requests' && !isSalaryVariant.value) {
        applyHopeFieldDefaults()
      }
    })
}

// 本人希望欄に入ったとき、および住所・AI推定結果が入ったときに初期値を適用する
watch(
  () => [
    props.currentCardId,
    props.basicData.address1,
    props.basicData.kibouGyoushuCd,
    props.basicData.kibouShokushuCd,
    props.basicData.gyoushuCd,
    props.basicData.shokushuCd
  ],
  () => {
    if (props.currentCardId !== 'requests' || isSalaryVariant.value) {
      return
    }
    runAutoRequestIfNeeded()
    refreshHopeFieldDefaults()
  },
  { immediate: true }
)

// 保存データの復元で希望勤務地・転職希望時期が差し替わったときは正規化と初期値だけやり直す。
// AI自動入力は上の watcher に任せる（ここで呼ぶと自身の書き込みで再発火して二重実行になる）。
// 正規化は冪等・初期値は空のときだけなので収束する
watch(
  () => [formatData.hopeTurnTime, formatData.hopeWorkAreas.join(',')],
  () => {
    if (props.currentCardId !== 'requests' || isSalaryVariant.value) {
      return
    }
    refreshHopeFieldDefaults()
  }
)

// 上の欄の1件目を basicData の希望業種・希望職種（ビズリーチ送客・RT 判定が読む値）に写す。
// 欄を初期化する前（hopeSelectAutofilled=false）の空欄では既存値を消さない（選んだ値は写す）
watch(
  () => [
    formatData.hopeIndustryCds[0] || '',
    formatData.hopeJobCategoryCds[0] || '',
    formatData.hopeSelectAutofilled
  ],
  ([industry, job, initialized]) => {
    if (!isHopeListFormat.value) return
    const payload: Partial<BasicData> = {}
    if (
      (industry || initialized) &&
      industry !== (props.basicData.kibouGyoushuCd || '')
    ) {
      payload.kibouGyoushuCd = industry as string
    }
    if (
      (job || initialized) &&
      job !== (props.basicData.kibouShokushuCd || '')
    ) {
      payload.kibouShokushuCd = job as string
    }
    if (Object.keys(payload).length > 0) onUpdateBasicData(payload)
  }
)

// 一覧の入力欄が無い書式では、画面に出ている希望業種・職種（AI カードで直せる kibou*）を
// 一覧の1件目に写す。送客は一覧を優先して読むので、写さないと古い一覧が送られる。一覧が空なら触らない。
// 保存データの復元で一覧が後から入る場合もあるので一覧の先頭も見る
watch(
  () => [
    props.basicData.kibouGyoushuCd || '',
    props.basicData.kibouShokushuCd || '',
    formatData.hopeIndustryCds[0] || '',
    formatData.hopeJobCategoryCds[0] || ''
  ],
  ([industry, job]) => {
    if (isHopeListFormat.value || isSalaryVariant.value) return
    const industries = syncHopeListHead(formatData.hopeIndustryCds, industry)
    if (industries) formatData.hopeIndustryCds = industries
    const jobs = syncHopeListHead(formatData.hopeJobCategoryCds, job)
    if (jobs) formatData.hopeJobCategoryCds = jobs
  },
  { immediate: true }
)

function onUpdateBasicData(payload: Partial<BasicData>) {
  Object.assign(props.basicData, payload)
  emit('update-basic-data', payload)
}

function onSelectExample(item: RirekishoExampleSentence) {
  formatData.request +=
    item.description
      .replace(/<br>/gi, '\n')
      .replace('OOO', formatData.nowNensyu)
      .replace('XXX', formatData.kibouNensyu) + '\n'
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/format-edit/rirekisho-guide';

// 下の項目との間隔を他の入力欄（item-body の下 0.5rem + 次の項目の上 0.5rem）にそろえる
.requests-card__free-text {
  margin-bottom: 0.5rem;
}
</style>
